import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api, ApiError } from './api';
import { useToast } from '../components/Toast';

export interface Me { userId: string; displayName: string; roles: string[]; permissions: string[]; isExternal: boolean; employerOrgId: string | null; lockedPages?: string[]; isPlatformAdmin?: boolean }
export interface ProjectInfo { project: any; setup: any[]; me: Me }

const Ctx = createContext<{ pid: string; info: ProjectInfo; can: (p: string) => boolean } | null>(null);

export function ProjectProvider({ pid, children }: { pid: string; children: ReactNode }) {
  const q = useQuery({ queryKey: ['project', pid], queryFn: () => api.get<ProjectInfo>(`/api/projects/${pid}`) });
  const value = useMemo(() => (q.data ? { pid, info: q.data, can: (p: string) => q.data!.me.permissions.includes(p) } : null), [pid, q.data]);
  if (q.isLoading) return <div className="page"><div className="skeleton" style={{ width: 240 }} /></div>;
  if (q.error) return <div className="page"><div className="banner crit">{(q.error as Error).message}</div></div>;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProject() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useProject outside provider');
  return v;
}

/** GET a project-scoped endpoint. `path` like '/turbines'. */
export function usePQ<T = any>(path: string | null, opts: { refetchInterval?: number; enabled?: boolean } = {}) {
  const { pid } = useProject();
  return useQuery<T>({ queryKey: [pid, path], queryFn: () => api.get<T>(`/api/projects/${pid}${path}`), enabled: path !== null && (opts.enabled ?? true), refetchInterval: opts.refetchInterval });
}

/** Mutation helper: posts to a project path; invalidates the project's cache; toasts results. */
export function usePM<TBody = any, TRes = any>(method: 'post' | 'patch' | 'put' | 'del', pathOf: string | ((b: TBody) => string), opts: { success?: string | ((r: TRes) => string); invalidate?: QueryKey[]; onSuccess?: (r: TRes) => void } = {}) {
  const { pid } = useProject();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<TRes, ApiError, TBody>({
    mutationFn: (b: TBody) => {
      const path = typeof pathOf === 'function' ? pathOf(b) : pathOf;
      const url = `/api/projects/${pid}${path}`;
      return (method === 'del' ? api.del(url) : (api as any)[method](url, b)) as Promise<TRes>;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: [pid] });
      qc.invalidateQueries({ queryKey: ['project', pid] });
      const msg = typeof opts.success === 'function' ? opts.success(r) : opts.success;
      if (msg) toast.ok(msg);
      opts.onSuccess?.(r);
    },
    onError: (e) => toast.err(e.message, Array.isArray(e.detail) ? (e.detail as any[]).map((d) => (typeof d === 'string' ? d : d.message ?? d.reason ?? d.title ?? JSON.stringify(d))) : undefined),
  });
}
